import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/brand-kit-creator';

const BrandKitCreatorLanding: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const title = 'Brand Kit Creator - Complete Brand Identity Suite | Explanatum';
    const desc = 'Create your complete brand identity in one place: AI business name generation, professional logo design, email signatures, letterhead, and compliments slips. Everything flows together seamlessly.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is included in the Brand Kit Creator?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The Brand Kit Creator includes 6 professional tools: Business Name Generator with taglines, AI Logo Designer with 4 variations, Email Signature Creator with 5 templates, Professional Letterhead with 4 templates, Compliments Slip Generator with 4 templates, and Complete Brand Kit Download (ZIP). All tools work together seamlessly with automatic data flow.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does the data flow between tools?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Type your business name once and it automatically populates in all other tools. Select your logo and it embeds in your email signature, letterhead, and compliments slip. Your brand colors flow through all templates. No repetitive data entry required.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I use the tools independently?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! Every tool is independently accessible. Use just the logo generator, or only the letterhead creator. Or use them all together in one seamless workflow. All tools have direct URLs and can be bookmarked individually.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does it cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The complete Brand Kit Creator is included with your Explanatum subscription. Instead of paying £500-£2,000 for separate services (naming, logo design, stationery design), you get everything unlimited for one price.',
        },
      },
      {
        '@type': 'Question',
        name: 'What formats can I download?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Download individual assets (PNG logos, HTML signatures) or get everything in one ZIP file including: business info, logo files, email signature HTML, letterhead template, compliments slip, brand colors reference, and README guide.',
        },
      },
      {
        '@type': 'Question',
        name: 'How fast can I create my brand identity?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most users complete their entire brand identity in 10-15 minutes. That includes naming, logo, email signature, and all stationery. Compare that to weeks waiting for agencies or freelancers.',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMTUwLDUwLDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm mb-6">
              <span className="text-3xl">🎨</span>
              <span className="text-sm font-semibold text-gray-700">Complete Brand Identity Suite</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
              Create Your Complete
              <span className="block bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">
                Brand Identity in Minutes
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Business name, AI logo, email signature, letterhead, and compliments slips—all in one seamless workflow. Everything flows together automatically.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => {
                  track('cta_click', { location: 'hero', tool: 'brand_kit_creator' });
                  navigate('/marketing/brand-kit-creator');
                }}
                className="px-8 py-4 bg-gradient-to-r from-orange-600 to-pink-600 text-white font-bold rounded-xl hover:from-orange-700 hover:to-pink-700 transition shadow-lg hover:shadow-xl text-lg"
              >
                Start Creating Your Brand →
              </button>
              <button
                onClick={() => {
                  const el = document.getElementById('features');
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-4 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition shadow-md text-lg"
              >
                See What's Included
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">6</div>
                <div className="text-sm text-gray-600">Professional Tools</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">10 min</div>
                <div className="text-sm text-gray-600">Complete Brand Kit</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">£2k+</div>
                <div className="text-sm text-gray-600">Value Included</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">6 Professional Tools in One Suite</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need for a complete, professional brand identity
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Tool 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Business Name Generator</h3>
              <p className="text-gray-600 mb-4">AI-powered names with explanations and taglines. Pick your favorite and it flows to all other tools.</p>
              <div className="text-sm text-blue-600 font-semibold">Value: £200-500</div>
            </div>

            {/* Tool 2 */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">AI Logo Designer</h3>
              <p className="text-gray-600 mb-4">Generate 4 professional logo variations instantly. Download high-res files and auto-embed in all stationery.</p>
              <div className="text-sm text-purple-600 font-semibold">Value: £300-1,000</div>
            </div>

            {/* Tool 3 */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Email Signature</h3>
              <p className="text-gray-600 mb-4">5 professional templates with auto-filled info and embedded logo. Copy HTML or export as image.</p>
              <div className="text-sm text-green-600 font-semibold">Value: £50-150</div>
            </div>

            {/* Tool 4 */}
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 border border-orange-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Letterhead Creator</h3>
              <p className="text-gray-600 mb-4">4 professional letterhead templates with logo integration. Print-ready PDF export for official documents.</p>
              <div className="text-sm text-orange-600 font-semibold">Value: £100-300</div>
            </div>

            {/* Tool 5 */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border border-cyan-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Compliments Slip</h3>
              <p className="text-gray-600 mb-4">Professional note cards for packages and documents. 4 elegant templates, A6 size, print-ready.</p>
              <div className="text-sm text-cyan-600 font-semibold">Value: £50-150</div>
            </div>

            {/* Tool 6 */}
            <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Complete Brand Kit</h3>
              <p className="text-gray-600 mb-4">Download everything in one ZIP: logos, signatures, letterhead, colors, and guide. Ready to use.</p>
              <div className="text-sm text-gray-700 font-semibold">Complete Package</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Seamless workflow or independent tool access</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                { step: 1, title: 'Generate Business Name', desc: 'AI creates unique names with taglines. Pick your favorite → auto-fills everywhere.', time: '2 min' },
                { step: 2, title: 'Design Your Logo', desc: 'Business name pre-filled. Get 4 AI variations → select best → auto-embeds in all tools.', time: '3 min' },
                { step: 3, title: 'Create Email Signature', desc: 'Name + logo already attached. Choose template → customize → copy HTML.', time: '2 min' },
                { step: 4, title: 'Generate Letterhead', desc: 'Everything pre-filled. Pick template → generate → print/PDF export.', time: '1 min' },
                { step: 5, title: 'Add Compliments Slip', desc: 'Optional professional touch. Pick template → generate → print/PDF.', time: '1 min' },
                { step: 6, title: 'Download Complete Kit', desc: 'Get everything in one ZIP. Logos, signatures, letterhead, colors, guide.', time: '1 min' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                    {item.step}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                  <div className="flex-shrink-0 text-sm font-semibold text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                    {item.time}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-gradient-to-r from-orange-100 to-pink-100 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Total Time: ~10 Minutes</h3>
              <p className="text-gray-700 mb-6">Complete professional brand identity vs. weeks with agencies</p>
              <button
                onClick={() => {
                  track('cta_click', { location: 'how_it_works', tool: 'brand_kit_creator' });
                  navigate('/marketing/brand-kit-creator');
                }}
                className="px-8 py-3 bg-gradient-to-r from-orange-600 to-pink-600 text-white font-bold rounded-xl hover:from-orange-700 hover:to-pink-700 transition shadow-lg"
              >
                Get Started Now →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {faqSchema.mainEntity.map((faq, idx) => (
              <details key={idx} className="group bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex justify-between items-center">
                  {faq.name}
                  <span className="text-orange-600 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">{faq.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection />

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-orange-600 via-pink-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Create Your Brand Identity?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join thousands of businesses using Explanatum to build professional brands in minutes, not weeks.
          </p>
          <button
            onClick={() => {
              track('cta_click', { location: 'footer', tool: 'brand_kit_creator' });
              navigate('/marketing/brand-kit-creator');
            }}
            className="px-10 py-4 bg-white text-orange-600 font-bold rounded-xl hover:bg-gray-100 transition shadow-xl text-lg"
          >
            Start Creating Your Brand →
          </button>
        </div>
      </section>
    </div>
  );
};

export default BrandKitCreatorLanding;
